import {
  allowedTransitionsFor,
  calculateOrderPosition,
  calculateOrderTotals,
  INTERNAL_CUSTOMER_NUMBER,
  normalizeOrderInput,
  normalizeOrderSearchInput,
  orderBucketForStatus,
  type CreateOrderDocumentInput,
  type OrderAddressInput,
  type OrderCustomerOption,
  type OrderDocumentKind,
  type OrderDocumentRecord,
  type OrderGateway,
  type OrderItemOption,
  type OrderMachineOption,
  type OrderOverview,
  type OrderPage,
  type OrderProfile,
  type OrderRecord,
  type OrderSearchInput,
  type OrderStatus,
  type SaveOrderInput,
  type TransitionOrderInput,
} from '@forq/orders-data';

interface OrdersTenantState {
  readonly orders: OrderRecord[];
  counter: number;
  readonly documents: OrderDocumentRecord[];
  readonly docCounters: Record<OrderDocumentKind, number>;
}

const documentNumberPrefix: Record<OrderDocumentKind, string> = {
  offer: 'AN-2026-', confirmation: 'AB-2026-', 'delivery-note': 'LS-2026-', 'workshop-order': 'WA-2026-',
};

function clone<T>(value: T): T { return structuredClone(value); }
function now(): string { return new Date().toISOString(); }
function prefixFor(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}
function orderNumber(counter: number): string { return `2026${String(counter).padStart(5, '0')}`; }

function address(kind: 'billing' | 'delivery' | 'site', label: string, companyName: string, city: string): OrderAddressInput {
  return { kind, label, companyName, street: 'Musterstraße 12', postalCode: city === 'Werl' ? '59457' : '59494', city, countryCode: 'DE' };
}

/** Name + Ort des eigenen Mandanten -- der interne Kunde 69999, auf den interne Aufträge laufen. */
function ownFirm(prefix: '1' | '2' | '3'): { readonly name: string; readonly city: string } {
  if (prefix === '2') return { name: 'Spezialtransporte Werl', city: 'Werl' };
  if (prefix === '3') return { name: 'Gabelstapler Sauerland GmbH', city: 'Arnsberg' };
  return { name: 'Fördertechnik Werl GmbH', city: 'Werl' };
}

function customersFor(tenantId: string): readonly OrderCustomerOption[] {
  const prefix = prefixFor(tenantId);
  const rows = prefix === '2'
    ? [['Westfalen Schwerlast GmbH', 'Werl', 'Andreas Becker'], ['Meyer Maschinenlogistik KG', 'Soest', 'Sarah Meyer']]
    : prefix === '3'
      ? [['Sauerland Holztechnik GmbH', 'Arnsberg', 'Daniel Schulte'], ['Metallbau Arnsberg KG', 'Meschede', 'Nina Kramer']]
      : [['Werl Industriebedarf GmbH', 'Werl', 'Martin Schulz'], ['Bäckerei Haverkamp GmbH', 'Soest', 'Julia Haverkamp']];
  const regular = rows.map(([displayName, city, contactName], index) => ({
    id: `${tenantId}-order-customer-${index + 1}`,
    number: `${prefix}${String(index + 1).padStart(4, '0')}`,
    displayName,
    contactName,
    email: `${contactName.split(' ')[0].toLowerCase()}@example.test`,
    phone: `02922 90${index + 1}0`,
    billingAddress: address('billing', 'Rechnungsadresse', displayName, city),
    deliveryAddress: index === 0 ? address('delivery', 'Lieferadresse', displayName, city) : null,
  }));
  const firm = ownFirm(prefix);
  const internal: OrderCustomerOption = {
    id: `${tenantId}-order-customer-internal`,
    number: INTERNAL_CUSTOMER_NUMBER,
    displayName: `${firm.name} (intern)`,
    contactName: '',
    email: '',
    phone: '',
    billingAddress: address('billing', 'Rechnungsadresse', firm.name, firm.city),
    deliveryAddress: null,
  };
  return [internal, ...regular];
}

function machinesFor(tenantId: string): readonly OrderMachineOption[] {
  const prefix = prefixFor(tenantId);
  const customers = customersFor(tenantId);
  return [
    { id: `${tenantId}-order-machine-1`, number: `${prefix}0001`, customerId: customers[0].id, manufacturer: 'Toyota', model: '8FBE16T', serialNumber: `TOY-${prefix}001`, status: 'customer' },
    { id: `${tenantId}-order-machine-2`, number: `${prefix}0002`, customerId: null, manufacturer: 'Jungheinrich', model: 'ETV 225', serialNumber: `JH-${prefix}002`, status: 'stock' },
  ];
}

function itemsFor(tenantId: string): readonly OrderItemOption[] {
  const prefix = prefixFor(tenantId);
  return [
    { id: `${tenantId}-order-item-1`, number: `${prefix}0101`, kind: 'article', name: 'Hydraulikfilter', unit: 'Stück', salesPrice: 84.5, purchasePrice: 46, vatRate: 19 },
    { id: `${tenantId}-order-item-2`, number: `${prefix}0102`, kind: 'article', name: 'Ladegerät 48 V', unit: 'Stück', salesPrice: 2400, purchasePrice: 1750, vatRate: 19 },
    { id: `${tenantId}-order-item-3`, number: `${prefix}0201`, kind: 'service', name: 'Servicetechniker', unit: 'Stunde', salesPrice: 98, purchasePrice: 0, vatRate: 19 },
    { id: `${tenantId}-order-item-4`, number: `${prefix}0202`, kind: 'service', name: 'UVV-Prüfung', unit: 'Pauschale', salesPrice: 180, purchasePrice: 0, vatRate: 19 },
  ];
}

function profileTitle(profile: OrderProfile): string {
  const labels: Record<OrderProfile, string> = {
    general: 'Normaler Vorgang', service: 'Serviceauftrag', internal: 'Interner Auftrag', sales: 'Verkaufsauftrag',
    rental: 'Mietauftrag', 'spare-parts': 'Ersatzteilverkauf', accessories: 'Zubehörverkauf', 'trade-in': 'Inzahlungnahme',
  };
  return labels[profile];
}

function createOrderRecord(
  tenantId: string,
  number: string,
  input: SaveOrderInput,
  current?: OrderRecord,
): OrderRecord {
  const customer = input.customerId ? customersFor(tenantId).find((item) => item.id === input.customerId) : null;
  if (input.customerId && !customer) throw new Error('Der ausgewählte Kunde gehört nicht zu diesem Mandanten.');
  const machine = input.machineId ? machinesFor(tenantId).find((item) => item.id === input.machineId) : null;
  if (input.machineId && !machine) throw new Error('Die ausgewählte Maschine gehört nicht zu diesem Mandanten.');
  if (machine?.customerId && customer && machine.customerId !== customer.id && input.profile === 'service') {
    throw new Error('Die Kundenmaschine gehört nicht zum ausgewählten Kunden.');
  }
  const positions = input.positions.map((position, index) => calculateOrderPosition(
    position,
    index + 1,
    current?.positions[index]?.id ?? `${tenantId}-order-position-${crypto.randomUUID()}`,
  ));
  const orderDiscount = Number(input.conditions.orderDiscountPercent ?? 0);
  const totals = calculateOrderTotals(positions, orderDiscount);
  const timestamp = now();
  const conditions = {
    customerReference: String(input.conditions.customerReference ?? ''),
    paymentTermDays: Number(input.conditions.paymentTermDays ?? 14),
    cashDiscountPercent: Number(input.conditions.cashDiscountPercent ?? 0),
    cashDiscountDays: Number(input.conditions.cashDiscountDays ?? 0),
    orderDiscountPercent: orderDiscount,
    requestedDate: input.conditions.requestedDate ?? null,
    deliveryMethod: String(input.conditions.deliveryMethod ?? ''),
    deliveryTerms: String(input.conditions.deliveryTerms ?? ''),
    documentDate: input.conditions.documentDate ?? null,
    language: String(input.conditions.language ?? ''),
    costCenterId: input.conditions.costCenterId ?? null,
    costCenterName: String(input.conditions.costCenterName ?? ''),
  };
  const status = current?.status ?? 'new';
  return {
    id: current?.id ?? `${tenantId}-order-${crypto.randomUUID()}`,
    tenantId,
    number,
    profile: input.profile,
    title: input.title || profileTitle(input.profile),
    status,
    bucket: orderBucketForStatus(status),
    priority: input.priority ?? 'normal',
    customerId: customer?.id ?? null,
    customerNumber: customer?.number ?? '',
    customerName: customer?.displayName ?? 'Ohne Kundenbezug',
    contactName: input.contactName || customer?.contactName || '',
    contactEmail: input.contactEmail || customer?.email || '',
    contactPhone: input.contactPhone || customer?.phone || '',
    machineId: machine?.id ?? null,
    machineNumber: machine?.number ?? '',
    machineLabel: machine ? `${machine.manufacturer} ${machine.model}` : '',
    assignedTo: input.assignedTo ?? '',
    dueAt: input.dueAt ?? null,
    notes: input.notes ?? '',
    addresses: input.addresses.map((item, index) => ({ ...item, id: current?.addresses[index]?.id ?? `${tenantId}-order-address-${crypto.randomUUID()}` })),
    conditions,
    positions,
    ...totals,
    statusHistory: current?.statusHistory ?? [{
      id: `${tenantId}-order-history-${crypto.randomUUID()}`,
      fromStatus: null,
      toStatus: status,
      reason: 'Vorgang angelegt',
      actor: 'Frederick',
      occurredAt: timestamp,
    }],
    createdAt: current?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
}

function seedOrder(
  tenantId: string,
  number: string,
  profile: OrderProfile,
  status: OrderStatus,
  customerIndex: number,
  title: string,
): OrderRecord {
  const customer = customersFor(tenantId)[customerIndex];
  const machine = machinesFor(tenantId)[0];
  const service = itemsFor(tenantId)[2];
  const article = itemsFor(tenantId)[0];
  const created = createOrderRecord(tenantId, number, {
    tenantId,
    profile,
    title,
    customerId: customer.id,
    contactName: customer.contactName,
    contactEmail: customer.email,
    contactPhone: customer.phone,
    machineId: profile === 'service' ? machine.id : null,
    priority: status === 'new' ? 'high' : 'normal',
    assignedTo: status === 'in-progress' ? 'Markus Beispiel' : '',
    dueAt: status === 'completed' ? '2026-07-10' : '2026-07-16',
    addresses: [customer.billingAddress, customer.deliveryAddress ?? customer.billingAddress],
    conditions: { paymentTermDays: 14, cashDiscountPercent: 2, cashDiscountDays: 7, orderDiscountPercent: 0 },
    positions: profile === 'service'
      ? [
          { kind: 'service', sourceId: service.id, sourceNumber: service.number, description: service.name, quantity: 2, unit: service.unit, unitPrice: service.salesPrice, vatRate: service.vatRate },
          { kind: 'article', sourceId: article.id, sourceNumber: article.number, description: article.name, quantity: 1, unit: article.unit, unitPrice: article.salesPrice, purchasePrice: article.purchasePrice, vatRate: article.vatRate },
        ]
      : [{ kind: 'text', description: 'Vorgangsdaten werden in der Workbench vervollständigt.' }],
  });
  const timestamp = '2026-07-13T08:00:00.000Z';
  return {
    ...created,
    status,
    bucket: orderBucketForStatus(status),
    statusHistory: [{ id: `${tenantId}-seed-history-${status}`, fromStatus: null, toStatus: status, reason: 'Demodaten', actor: 'System', occurredAt: timestamp }],
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

function createSeed(tenantId: string): OrdersTenantState {
  return {
    orders: [
      seedOrder(tenantId, orderNumber(132), 'service', 'new', 0, 'Störung Toyota 8FBE16T'),
      seedOrder(tenantId, orderNumber(131), 'spare-parts', 'in-progress', 1, 'Ersatzteilauftrag Hydraulik'),
      seedOrder(tenantId, orderNumber(128), 'sales', 'completed', 0, 'Verkaufsauftrag Elektrostapler'),
    ],
    counter: 133,
    documents: [],
    docCounters: { offer: 1, confirmation: 1, 'delivery-note': 1, 'workshop-order': 1 },
  };
}

export class DemoOrdersGateway implements OrderGateway {
  private readonly states = new Map<string, OrdersTenantState>();

  private state(tenantId: string): OrdersTenantState {
    const existing = this.states.get(tenantId);
    if (existing) return existing;
    const created = createSeed(tenantId);
    this.states.set(tenantId, created);
    return created;
  }

  async getOverview(tenantId: string): Promise<OrderOverview> {
    const orders = this.state(tenantId).orders;
    return {
      newCount: orders.filter((item) => item.bucket === 'new').length,
      inProgressCount: orders.filter((item) => item.bucket === 'in-progress').length,
      completedCount: orders.filter((item) => item.bucket === 'completed').length,
      billingReadyCount: orders.filter((item) => item.status === 'billing-ready').length,
    };
  }

  async searchOrders(input: OrderSearchInput): Promise<OrderPage> {
    const normalized = normalizeOrderSearchInput(input);
    const needle = normalized.query.toLowerCase();
    const filtered = this.state(normalized.tenantId).orders
      .filter((item) => normalized.bucket === 'all' || item.bucket === normalized.bucket)
      .filter((item) => normalized.profile === 'all' || item.profile === normalized.profile)
      .filter((item) => !needle || [item.number, item.title, item.customerName, item.machineLabel, item.machineNumber, item.assignedTo]
        .some((value) => value.toLowerCase().includes(needle)))
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(filtered.slice(start, start + normalized.pageSize)), total: filtered.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async getOrder(tenantId: string, orderId: string): Promise<OrderRecord> {
    const found = this.state(tenantId).orders.find((item) => item.id === orderId);
    if (!found) throw new Error('Der Vorgang wurde in diesem Mandanten nicht gefunden.');
    return clone(found);
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly OrderCustomerOption[]> {
    const needle = query.trim().toLowerCase();
    return clone(customersFor(tenantId).filter((item) => !needle || [item.number, item.displayName, item.contactName]
      .some((value) => value.toLowerCase().includes(needle))));
  }

  async searchMachineOptions(tenantId: string, query = '', customerId: string | null = null, includeAll = false): Promise<readonly OrderMachineOption[]> {
    const needle = query.trim().toLowerCase();
    return clone(machinesFor(tenantId)
      .filter((item) => includeAll || !customerId || item.customerId === customerId)
      .filter((item) => !needle || [item.number, item.manufacturer, item.model, item.serialNumber]
        .some((value) => value.toLowerCase().includes(needle))));
  }

  async searchItemOptions(tenantId: string, query = ''): Promise<readonly OrderItemOption[]> {
    const needle = query.trim().toLowerCase();
    return clone(itemsFor(tenantId).filter((item) => !needle || [item.number, item.name, item.kind]
      .some((value) => value.toLowerCase().includes(needle))));
  }

  async saveOrder(rawInput: SaveOrderInput): Promise<OrderRecord> {
    const input = normalizeOrderInput(rawInput);
    const state = this.state(input.tenantId);
    if (input.orderId) {
      const index = state.orders.findIndex((item) => item.id === input.orderId);
      if (index < 0) throw new Error('Der Vorgang wurde in diesem Mandanten nicht gefunden.');
      if (['completed', 'cancelled'].includes(state.orders[index].status)) throw new Error('Abgeschlossene Vorgänge können nicht mehr bearbeitet werden.');
      const updated = createOrderRecord(input.tenantId, state.orders[index].number, input, state.orders[index]);
      state.orders[index] = updated;
      return clone(updated);
    }
    const created = createOrderRecord(input.tenantId, orderNumber(state.counter++), input);
    state.orders.unshift(created);
    return clone(created);
  }

  async transitionOrder(input: TransitionOrderInput): Promise<OrderRecord> {
    const state = this.state(input.tenantId);
    const index = state.orders.findIndex((item) => item.id === input.orderId);
    if (index < 0) throw new Error('Der Vorgang wurde in diesem Mandanten nicht gefunden.');
    const current = state.orders[index];
    if (!allowedTransitionsFor(current.status).includes(input.toStatus)) {
      throw new Error(`Der Statuswechsel von ${current.status} zu ${input.toStatus} ist nicht zulässig.`);
    }
    const timestamp = now();
    const updated: OrderRecord = {
      ...current,
      status: input.toStatus,
      bucket: orderBucketForStatus(input.toStatus),
      statusHistory: [{
        id: `${input.tenantId}-order-history-${crypto.randomUUID()}`,
        fromStatus: current.status,
        toStatus: input.toStatus,
        reason: input.reason?.trim() || 'Status geändert',
        actor: 'Frederick',
        occurredAt: timestamp,
      }, ...current.statusHistory],
      updatedAt: timestamp,
    };
    state.orders[index] = updated;
    return clone(updated);
  }

  async createDocument(input: CreateOrderDocumentInput): Promise<OrderDocumentRecord> {
    const state = this.state(input.tenantId);
    const order = state.orders.find((item) => item.id === input.orderId);
    if (!order) throw new Error('Der Vorgang wurde in diesem Mandanten nicht gefunden.');
    const sequenceValue = state.docCounters[input.kind];
    state.docCounters[input.kind] = sequenceValue + 1;
    const document: OrderDocumentRecord = {
      id: `${input.tenantId}-order-doc-${crypto.randomUUID()}`,
      orderId: order.id,
      orderNumber: order.number,
      kind: input.kind,
      number: `${documentNumberPrefix[input.kind]}${String(sequenceValue).padStart(5, '0')}`,
      title: order.title,
      createdBy: 'Frederick',
      createdAt: now(),
      snapshot: clone(order),
    };
    state.documents.unshift(document);
    return clone(document);
  }

  async listDocuments(tenantId: string, orderId: string): Promise<readonly OrderDocumentRecord[]> {
    return clone(this.state(tenantId).documents.filter((document) => document.orderId === orderId));
  }

  /**
   * Entfernt einen Datensatz -- das Gegenstueck zum Loeschen in der Datenbank.
   * Ohne das meldet der Demo-Modus "geloescht", waehrend der Eintrag stehen bleibt.
   */
  removeOrder(tenantId: string, orderId: string): void {
    const zustand = this.states.get(tenantId);
    if (!zustand) return;
    const stelle = zustand.orders.findIndex((eintrag) => eintrag.id === orderId);
    if (stelle >= 0) zustand.orders.splice(stelle, 1);
  }
}

export function createDemoOrdersGateway(): DemoOrdersGateway {
  return new DemoOrdersGateway();
}
